import React from "react";
import { useWallet } from "../context/WalletContext";
import useWalletBalances from "../hooks/useWalletBalances";
import useLive from "../hooks/useLive";
import usePositions from "../hooks/usePositions";

const fmt = (v, d = 2) => (v == null ? "—" : Number(v).toFixed(d));

export default function Book() {
  const { wallet, setOpen } = useWallet();
  const { listed } = useLive();
  const rawAddr = wallet?.rawAddress;
  const chainId = wallet?.chainId;
  const { balances, eth } = useWalletBalances(rawAddr, chainId);
  const { rows: ranges, loading: rangesLoading } = usePositions(rawAddr, chainId);

  if (!wallet) {
    return (
      <div style={{ maxWidth: 1520, margin: "0 auto", padding: "48px 28px 40px" }}>
        <h1 className="serif" style={{ fontSize: 52, letterSpacing: "-0.02em", margin: "0 0 40px" }} data-testid="book-title">Book.</h1>
        <div style={{ borderTop: "1px solid var(--line)", borderBottom: "1px solid var(--line)", padding: "72px 0", textAlign: "center" }}>
          <div className="serif" style={{ fontSize: 30, marginBottom: 18 }}>Connect to see the book.</div>
          <button className="btn-primary" onClick={() => setOpen(true)} data-testid="book-connect">Connect</button>
        </div>
      </div>
    );
  }

  const isDemo = wallet.mode === "demo";
  const wrongNetwork = wallet.chainId !== 4663;

  // Build positions from balances (real) or demo values
  const stockPos = balances
    ? Object.entries(balances)
        .filter(([sym]) => sym !== "USDG")
        .map(([sym, size]) => {
          const row = listed.find((r) => r.ticker === sym);
          const pool = row?.pool;
          const notional = pool != null ? size * pool : null;
          return { kind: "listed", ticker: sym, size, price: pool, notional };
        })
        .filter((p) => p.size > 1e-8)
    : [];

  return (
    <div style={{ maxWidth: 1520, margin: "0 auto", padding: "48px 28px 40px" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 20 }}>
        <h1 className="serif" style={{ fontSize: 52, letterSpacing: "-0.02em", margin: 0 }} data-testid="book-title">Book.</h1>
        <div style={{ display: "flex", alignItems: "baseline", gap: 20 }}>
          <div style={{ fontSize: 11, letterSpacing: "0.14em", textTransform: "uppercase", color: "var(--ink-muted)" }}>
            {isDemo ? "Demo wallet" : "Wallet"} {wallet.address}
          </div>
          <div className="tabular" style={{ fontSize: 22 }}>
            <span style={{ color: "var(--ink-muted)", fontSize: 11, letterSpacing: "0.14em", textTransform: "uppercase", marginRight: 8 }}>USDG</span>
            {isDemo ? "12,480.42" : fmt(balances?.USDG)}
          </div>
        </div>
      </div>

      {wrongNetwork && (
        <div style={{ borderTop: "1px solid var(--down)", borderBottom: "1px solid var(--down)", padding: "12px 0", color: "var(--down)", fontSize: 13, marginBottom: 24 }} data-testid="wrong-network">
          Wallet is on chain {wallet.chainId}. Switch to 4663 to read balances.
        </div>
      )}

      {/* Balances */}
      <div style={{ marginTop: 24 }}>
        <div className="grotesque" style={{ fontSize: 11, letterSpacing: "0.18em", textTransform: "uppercase", color: "var(--ink-muted)", marginBottom: 10 }}>
          Balances{isDemo && " · demo"}
        </div>
        <table className="book tabular" data-testid="book-positions">
          <thead><tr><th>Asset</th><th>Size</th><th>Pool</th><th>Notional (USDG)</th></tr></thead>
          <tbody>
            <tr>
              <td>ETH <span style={{ color: "var(--ink-muted)" }}>(gas)</span></td>
              <td>{isDemo ? "1.842" : fmt(eth, 4)}</td>
              <td style={{ color: "var(--ink-muted)" }}>—</td>
              <td style={{ color: "var(--ink-muted)" }}>—</td>
            </tr>
            {isDemo ? (
              <>
                <tr><td>NVDA</td><td>12</td><td>{fmt(listed.find(r=>r.ticker==="NVDA")?.pool)}</td><td>{fmt(12 * (listed.find(r=>r.ticker==="NVDA")?.pool || 0))}</td></tr>
                <tr><td>SPY</td><td>4</td><td>{fmt(listed.find(r=>r.ticker==="SPY")?.pool)}</td><td>{fmt(4 * (listed.find(r=>r.ticker==="SPY")?.pool || 0))}</td></tr>
              </>
            ) : (
              stockPos.length === 0 ? (
                <tr><td colSpan={4} style={{ color: "var(--ink-muted)", padding: 24 }}>No listed positions.</td></tr>
              ) : (
                stockPos.map((p) => (
                  <tr key={p.ticker}>
                    <td>{p.ticker}</td>
                    <td>{fmt(p.size, 4)}</td>
                    <td>{fmt(p.price)}</td>
                    <td>{fmt(p.notional)}</td>
                  </tr>
                ))
              )
            )}
          </tbody>
        </table>
      </div>

      {/* Ranges — real Uniswap V3 NFT positions */}
      <div style={{ marginTop: 48 }}>
        <div className="grotesque" style={{ fontSize: 11, letterSpacing: "0.18em", textTransform: "uppercase", color: "var(--ink-muted)", marginBottom: 10 }}>
          Ranges {rangesLoading && "· reading…"}
        </div>
        <table className="book tabular" data-testid="book-ranges">
          <thead><tr><th>Token ID</th><th>Pair</th><th>Fee</th><th>Low</th><th>High</th><th>Pending fees</th><th>Liquidity</th><th></th></tr></thead>
          <tbody>
            {(!ranges || ranges.length === 0) ? (
              <tr><td colSpan={8} style={{ color: "var(--ink-muted)", padding: 24 }}>
                {isDemo ? "Demo wallet — no on-chain positions." : "No open ranges on chain 4663. Draft one from any ticket."}
              </td></tr>
            ) : (
              ranges.map((r) => (
                <tr key={r.tokenId}>
                  <td>#{r.tokenId}</td>
                  <td>{r.pair}</td>
                  <td style={{ color: "var(--ink-muted)" }}>{(r.fee/10000).toFixed(2)}%</td>
                  <td>{fmt(r.low)}</td>
                  <td>{fmt(r.high)}</td>
                  <td>
                    {r.feesUsdg != null ? (
                      <span className={r.feesUsdg > 0 ? "up" : ""}>{fmt(r.feesUsdg, 4)} USDG</span>
                    ) : r.fees0 != null && r.fees1 != null ? (
                      <span style={{ color: "var(--ink-muted)" }}>{fmt(r.fees0, 4)} {r.sym0} · {fmt(r.fees1, 4)} {r.sym1}</span>
                    ) : (
                      <span style={{ color: "var(--ink-muted)" }}>—</span>
                    )}
                  </td>
                  <td style={{ color: "var(--ink-muted)", fontSize: 11 }}>{r.liquidity && r.liquidity.length > 12 ? r.liquidity.slice(0,4) + "…" + r.liquidity.slice(-4) : r.liquidity}</td>
                  <td>
                    <a href={`https://robinhoodchain.blockscout.com/token/0x73991a25C818Bf1f1128deaab1492d45638de0d3/instance/${r.tokenId}`} target="_blank" rel="noreferrer" className="btn-ghost">Open</a>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Fee share */}
      <div style={{ marginTop: 56, borderTop: "1px solid var(--line)", paddingTop: 24 }} data-testid="fee-share">
        <div className="grotesque" style={{ fontSize: 11, letterSpacing: "0.18em", textTransform: "uppercase", color: "var(--ink-muted)", marginBottom: 20 }}>
          Fee share
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 40 }}>
          {[["Trades", 0], ["Pool", 0], ["Holders", 0]].map(([k, v]) => (
            <div key={k} style={{ borderTop: "1px solid var(--line)", paddingTop: 14 }}>
              <div className="grotesque" style={{ fontSize: 11, letterSpacing: "0.14em", textTransform: "uppercase", color: "var(--ink-muted)" }}>{k}</div>
              <div className="tabular" style={{ fontSize: 28, marginTop: 6 }}>{Number(v).toFixed(2)} <span style={{ fontSize: 12, color: "var(--ink-muted)" }}>USDG</span></div>
            </div>
          ))}
        </div>
        <div style={{ color: "var(--ink-muted)", fontSize: 12, marginTop: 12 }}>
          Fee share accrues when the pool prints. Read from chain.
        </div>
      </div>
    </div>
  );
}
